import {
  CalendarDots,
  CheckCircle,
  CircleDashed,
  PaperPlaneTilt,
  Percent,
  UserList,
  UsersThree,
} from "@phosphor-icons/react";
import { useEffect, useState } from "react";

import CampaignTypeIcon from "common/components/CampaignTypeIcon";
import { MobileSelectionListItem } from "common/components/MobileSelectionList";
import MobileSelectionList from "common/components/MobileSelectionList/MobileSelectionList";
import Pagination from "common/components/ui/Pagination";
import { Tag } from "common/components/ui/Tag";
import { CAMPAIGN_TYPES } from "common/constants";
import { formatDate } from "common/helpers/utils";
import type { Campaign } from "common/types";

import { CAMPAIGN_OUTREACH_OPTIONS } from "../../../constants";
import CampaignStateTag from "../../CampaignStateTag";
import WorkerAccountsTag from "../../WorkerAccountsTag";
import CampaignPreview from "./CampaignPreview";
import ProgressTag from "./ProgressTag";
import SuccessTag from "./SuccessTag";

const PAGE_SIZE = 10;

const MOBILE_TAGS = {
  accounts: {
    icon: <UserList weight="fill" />,
    label: "Accounts",
    renderTag: (campaign: Campaign) => (
      <WorkerAccountsTag
        owners={campaign.owners}
        isUsingNote={campaign.uses_connection_note}
        size="sm"
      />
    ),
    isDefault: true,
  },
  status: {
    icon: <CircleDashed weight="fill" />,
    label: "Status",
    renderTag: (campaign: Campaign) => (
      <CampaignStateTag
        isUsingNote={campaign.uses_connection_note}
        owners={campaign.owners}
        size="sm"
        startDate={campaign.start_date}
        state={campaign.state}
      />
    ),
    isDefault: true,
  },
  outreach_type: {
    icon: <PaperPlaneTilt weight="fill" />,
    label: "Campaign type",
    renderTag: (campaign: Campaign) => (
      <Tag size="sm" variant="tertiary">
        {campaign.outreach_type === "drip" ? "Inbound" : "Outbound"}
      </Tag>
    ),
  },
  created_at: {
    icon: <CalendarDots weight="fill" />,
    label: "Created at",
    renderTag: (campaign: Campaign) => (
      <Tag
        size="sm"
        leftIcon={<CalendarDots weight="fill" />}
        variant="tertiary"
      >
        {formatDate(campaign.created_at)}
      </Tag>
    ),
  },
  targetCount: {
    icon: <UsersThree weight="fill" />,
    label: "Targets",
    renderTag: ({ state, target_count }: Campaign) =>
      state !== "INIT" && (
        <Tag
          variant="tertiary"
          size="sm"
          leftIcon={<UsersThree weight="fill" />}
        >
          {target_count}
        </Tag>
      ),
  },
  completion: {
    icon: <Percent weight="fill" />,
    label: "Progress",
    renderTag: ({ id, state }: Campaign) =>
      !(state === "INIT" || state === "CREATED") && (
        <ProgressTag campaignId={id} size="sm" />
      ),
  },
  success: {
    icon: <CheckCircle weight="fill" />,
    label: "Success",
    renderTag: ({ id, outreach_type, state }: Campaign) =>
      !(state === "INIT" || state === "CREATED") && (
        <SuccessTag campaignId={id} outreachType={outreach_type} size="sm" />
      ),
    isDefault: true,
  },
} as const;

interface MobileListProps {
  campaigns: Campaign[];
  selectedCampaigns: Campaign[];
  setSelectedCampaigns: (selectedCampaigns: Campaign[]) => void;
}

export default function MobileList({
  campaigns,
  selectedCampaigns,
  setSelectedCampaigns,
}: MobileListProps) {
  const [page, setPage] = useState(0);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign>(null);

  useEffect(() => {
    setPage(0);
  }, [campaigns]);

  const pageItems = campaigns.slice(
    page * PAGE_SIZE,
    page * PAGE_SIZE + PAGE_SIZE,
  );

  return (
    <>
      <MobileSelectionList
        data={pageItems}
        selection={selectedCampaigns}
        onSelectionChange={(newSelection) => {
          if (newSelection === "all") {
            setSelectedCampaigns(campaigns);
          } else {
            setSelectedCampaigns(newSelection);
          }
        }}
        totalDataCount={campaigns.length}
        allTags={MOBILE_TAGS}
      >
        {(campaign, listItemProps) => {
          const { type, name, outreach_type: outreachType } = campaign;

          const { icon: SubtypeIcon } = CAMPAIGN_OUTREACH_OPTIONS[outreachType];
          const { label } = CAMPAIGN_TYPES[type];

          return (
            <MobileSelectionListItem
              onClick={() => {
                setSelectedCampaign(campaign);
                setIsPreviewOpen(true);
              }}
              {...listItemProps}
            >
              <CampaignTypeIcon
                type={type}
                variant={outreachType === "drip" ? "drip" : "default"}
                size="sm"
              />
              <div className="flex flex-col items-start">
                <h3 className="text-xs-bold text-left line-clamp-1">{name}</h3>
                <div className="flex items-center gap-x-1 text-black-500">
                  <SubtypeIcon size={16} />
                  <span className="line-clamp-1 break-all text-button-xs">
                    {label} • {outreachType === "drip" ? "1st" : "2nd & 3rd+"}{" "}
                    connections
                  </span>
                </div>
              </div>
            </MobileSelectionListItem>
          );
        }}
      </MobileSelectionList>

      <Pagination
        size="sm"
        pageSize={PAGE_SIZE}
        itemCount={campaigns.length}
        setPage={setPage}
        selectedIndex={page}
        isLastPageHidden={false}
      />

      {selectedCampaign && (
        <CampaignPreview
          isOpen={isPreviewOpen}
          setIsOpen={setIsPreviewOpen}
          campaign={selectedCampaign}
        />
      )}
    </>
  );
}
